/* eslint-disable import/no-extraneous-dependencies */

import {
  Area,
  AreaChart as AreaChartRecharts,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts'

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/shared/shadcn/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@/shared/shadcn/components/ui/chart'
import Select from '@/shared/components/Select/Select'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { dashboardEndDate, daysBefore } from '@/features/dashboard/utils/dateRange'

type ChartDataType = { date: string; [key: string]: number | string }
type ChartRow = { date: string; [key: string]: number | string }

type MultipleChartsDataType = {
  dataKey: string
  chartData: ChartDataType[]
}

interface AreaChartProps {
  title: string
  description?: string | React.ReactNode
  chartsData: MultipleChartsDataType[]
  flex?: number | 'auto'
  condensed?: boolean
  setStartDate?: Dispatch<SetStateAction<string>>
  isLoading: boolean
  loadingPlaceholder: JSX.Element
  /**
   * Extra classes for the card itself, for the caller that needs one of several
   * identical tiles to stand out. Appended last, so it wins on border and
   * background without the card having to know what "highlighted" means.
   */
  cardClassName?: string
}

const mergeSeriesByDate = (series: MultipleChartsDataType[]): ChartRow[] => {
  const map = new Map<string, ChartRow>()

  series.forEach(s => {
    s.chartData.forEach(point => {
      const key = point.date
      const row = map.get(key) ?? { date: key }
      row[s.dataKey] = point[s.dataKey]
      map.set(key, row)
    })
  })

  return Array.from(map.values()).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )
}

export function AreaChart(props: AreaChartProps) {
  const {
    title,
    description,
    chartsData,
    flex,
    condensed = false,
    setStartDate,
    isLoading = false,
    loadingPlaceholder,
    cardClassName = ''
  } = props
  const [timeRange, setTimeRange] = useState('90d')
  const { t } = useTranslation()

  const chartConfig = useMemo<ChartConfig>(() => {
    return {
      totalSales: {
        label: t('DASHBOARD.CHART.TOTAL_SALES'),
        color: 'var(--chart-1)'
      },
      profit: {
        label: t('DASHBOARD.CHART.PROFIT'),
        color: 'var(--foreground)'
      },
      cost: {
        label: t('DASHBOARD.CHART.COST'),
        color: 'var(--chart-2)'
      }
    }
  }, [t])

  let daysToSubtract
  switch (timeRange) {
    case '7d':
      daysToSubtract = 7
      break
    case '30d':
      daysToSubtract = 30
      break
    default:
      daysToSubtract = 90
  }

  const options = useMemo(
    () => [
      { label: t('DASHBOARD.LAST_3_MONTHS'), value: '90d' },
      { label: t('DASHBOARD.LAST_30_DAYS'), value: '30d' },
      { label: t('DASHBOARD.LAST_7_DAYS'), value: '7d' }
    ],
    [t]
  )

  const selected = useMemo(
    () => options.find(o => o.value === timeRange) ?? options[0],
    [options, timeRange]
  )

  const endDate = useMemo(dashboardEndDate, [])

  const newStartDate = useMemo(
    () => daysBefore(endDate, daysToSubtract),
    [endDate, daysToSubtract]
  )

  useEffect(() => {
    setStartDate?.(newStartDate)
  }, [newStartDate, setStartDate])

  const mergedData = mergeSeriesByDate(chartsData)

  const { yMin, yMax } = useMemo(() => {
    const values = chartsData
      .flatMap(s => s.chartData.map(p => Number(p[s.dataKey])))
      .filter(v => !Number.isNaN(v))

    if (values.length === 0) return { yMin: 0, yMax: 0 }
    return {
      yMin: Math.min(...values),
      yMax: Math.max(...values)
    }
  }, [chartsData])

  const flexClass = (() => {
    switch (flex) {
      case 1:
        return 'flex-1'
      case 2:
        return 'flex-2'
      case 'auto':
        return 'flex-auto'
      default:
        return 'flex-1'
    }
  })()

  const chartStrokeColors = [
    'var(--chart-1)',
    'var(--foreground)',
    'var(--chart-2)'
  ]

  const chartAreaColors = [
    'var(--chart-1)',
    'var(--foreground)',
    'var(--chart-2)'
  ]

  const gradientId = (index: number) => `areaFill-${index}`

  return (
    <Card
      className={`pt-0 gap-0 relative overflow-hidden ${flexClass} ${
        condensed ? 'pb-2 h-[160px]' : 'pb-4'
      } min-w-0 flex flex-col ${cardClassName}`}
    >
      {isLoading && loadingPlaceholder}
      <CardHeader
        className={`shrink-0 flex items-start gap-2 space-y-0 border-b py-5 sm:flex-row ${
          condensed ? 'py-3 border-b-0' : 'py-5'
        }`}
      >
        <div className="grid flex-1 gap-1">
          <CardTitle className="text-ellipsis">{title}</CardTitle>
          {!isLoading && description && (
            <CardDescription>{description}</CardDescription>
          )}
        </div>
        {condensed === false && (
          <Select
            options={options}
            value={selected}
            onChange={option => setTimeRange((option as OptionType).value)}
            minWidth="9rem"
            maxWidth="11rem"
            hideClearIcon
            widthFitContent
            readonly
          />
        )}
      </CardHeader>
      {!isLoading && (
        <CardContent
          className={`px-2 ${condensed ? 'pt-0' : 'pt-4'} sm:px-6 ${
            condensed ? 'sm:pt-0' : 'sm:pt-6'
          } flex-1 min-h-0`}
        >
          <ChartContainer
            config={chartConfig}
            className="h-full w-full min-h-0"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChartRecharts
                data={mergedData}
                margin={{
                  top: 5,
                  right: 0,
                  left: 0,
                  bottom: 10
                }}
              >
                <defs>
                  {chartAreaColors.map((color, index) => (
                    <linearGradient
                      key={gradientId(index)}
                      id={gradientId(index)}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                    </linearGradient>
                  ))}
                </defs>
                {!condensed && (
                  <CartesianGrid
                    vertical={false}
                    stroke="color-mix(in oklab, var(--border) 100%, transparent)"
                  />
                )}
                {!condensed && (
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tickFormatter={value => {
                      const date = new Date(value)
                      return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })
                    }}
                  />
                )}
                {condensed && (
                  <YAxis
                    hide
                    domain={[
                      yMin,
                      yMax - yMin === 0 ? yMax + 10 : yMax + (yMax - yMin) * 0.2
                    ]}
                  />
                )}
                {!condensed && (
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={value =>
                          new Date(value).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })
                        }
                        indicator="dot"
                      />
                    }
                  />
                )}
                {chartsData.map((series, index) => (
                  <Area
                    key={series.dataKey}
                    dataKey={series.dataKey}
                    type="monotone"
                    fill={`url(#${gradientId(index % chartAreaColors.length)})`}
                    stroke={chartStrokeColors[index % chartStrokeColors.length]}
                    isAnimationActive={false}
                    baseValue="dataMin"
                    connectNulls
                  />
                ))}
              </AreaChartRecharts>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      )}
    </Card>
  )
}
